import React, { useState } from 'react';
import {
  DocumentTextIcon,
  ClipboardDocumentCheckIcon,
  UserGroupIcon,
  BanknotesIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';

const tenderData = [
  { month: 'Jan', count: 12 },
  { month: 'Feb', count: 19 },
  { month: 'Mar', count: 15 },
  { month: 'Apr', count: 22 },
  { month: 'May', count: 25 },
  { month: 'Jun', count: 17 },
];

const contractValueData = [
  { month: 'Jan', value: 150000 },
  { month: 'Feb', value: 220000 },
  { month: 'Mar', value: 180000 },
  { month: 'Apr', value: 290000 },
  { month: 'May', value: 320000 },
  { month: 'Jun', value: 250000 },
];

const stats = [
  {
    id: 1,
    name: 'Open Tenders',
    value: '24',
    icon: DocumentTextIcon,
    change: '+8%',
    changeType: 'positive',
  },
  {
    id: 2,
    name: 'Active Contracts',
    value: '156',
    icon: ClipboardDocumentCheckIcon,
    change: '+12%',
    changeType: 'positive',
  },
  {
    id: 3,
    name: 'Registered Vendors',
    value: '2,345',
    icon: UserGroupIcon,
    change: '+3%',
    changeType: 'positive',
  },
  {
    id: 4,
    name: 'Total Contract Value',
    value: '$12.5M',
    icon: BanknotesIcon,
    change: '+15%',
    changeType: 'positive',
  },
];

const pendingApprovals = [
  {
    id: 1,
    title: 'Office Supplies Tender',
    type: 'Tender',
    status: 'Pending Review',
    deadline: '2024-04-15',
    value: '$25,000',
  },
  {
    id: 2,
    title: 'IT Equipment Contract',
    type: 'Contract',
    status: 'Awaiting Approval',
    deadline: '2024-04-12',
    value: '$150,000',
  },
  {
    id: 3,
    title: 'Cleaning Services',
    type: 'Tender',
    status: 'Under Evaluation',
    deadline: '2024-04-20',
    value: '$45,000',
  },
];

export default function Dashboard() {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const sortedApprovals = React.useMemo(() => {
    if (!sortConfig.key) return pendingApprovals;

    return [...pendingApprovals].sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'ascending' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'ascending' ? 1 : -1;
      }
      return 0;
    });
  }, [sortConfig]);

  return (
    <div className="min-h-screen w-full bg-gray-50">
      <div className="max-w-full mx-auto space-y-8 p-6">
        {/* Page Header */}
        <div className="border-b pb-4 bg-white rounded-lg shadow-sm p-6  flex-col">
          <h1 className="text-2xl font-semibold text-gray-900" tabIndex="0">Dashboard Overview</h1>
          <p className="mt-2 text-sm text-gray-600">A summary of your procurement activities and key metrics.</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {stats.map((item) => (
            <div 
              key={item.id} 
              className="bg-white overflow-hidden rounded-lg shadow-sm border hover:shadow-md transition-shadow duration-200"
              role="region"
              aria-label={`${item.name} statistics`}
            >
              <div className="p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <item.icon className="h-8 w-8 text-gray-400" aria-hidden="true" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">{item.name}</dt>
                      <dd className="flex items-baseline">
                        <div className="text-2xl font-semibold text-gray-900">{item.value}</div>
                        <div className={`ml-2 flex items-baseline text-sm font-semibold ${
                          item.changeType === 'positive' ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {item.change}
                        </div>
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Tender Activity Chart */}
          <div className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow duration-200">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900">Tender Activity</h3>
              <p className="mt-1 text-sm text-gray-500">Monthly overview of tender submissions</p>
              <div className="mt-6 h-80" role="img" aria-label="Bar chart showing tender activity by month">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={tenderData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="#4F46E5" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Contract Value Trend */}
          <div className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow duration-200">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900">Contract Value Trend</h3>
              <p className="mt-1 text-sm text-gray-500">Monthly contract values in USD</p>
              <div className="mt-6 h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={contractValueData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Line 
                      type="monotone" 
                      dataKey="value" 
                      stroke="#4F46E5" 
                      strokeWidth={2}
                      dot={{ r: 4, fill: "#4F46E5" }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>

        {/* Pending Approvals */}
        <div className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow duration-200">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Pending Approvals</h3>
                <p className="mt-1 text-sm text-gray-500">Items requiring your attention</p>
              </div>
              <button 
                className="px-4 py-2 text-sm font-medium text-indigo-600 hover:text-indigo-500 hover:bg-indigo-50 rounded-md transition-colors"
                onClick={() => console.log('View all clicked')}
                aria-label="View all pending approvals"
              >
                View all
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200" role="table">
                <thead>
                  <tr>
                    {['Title', 'Type', 'Status', 'Deadline', 'Value'].map((header) => (
                      <th 
                        key={header}
                        className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort(header.toLowerCase())}
                        role="columnheader"
                        aria-sort={sortConfig.key === header.toLowerCase() ? sortConfig.direction : 'none'}
                      >
                        <div className="flex items-center space-x-1">
                          <span>{header}</span>
                          {sortConfig.key === header.toLowerCase() && (
                            <span>{sortConfig.direction === 'ascending' ? '↑' : '↓'}</span>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sortedApprovals.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50 cursor-pointer">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {item.title}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.type}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          {item.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(item.deadline)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.value}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}